const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDIA_ORIGIN = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const TOKEN_KEY = "td_session_token";

// Normalizes an image/file URL so it resolves against the CURRENT backend host.
// Uploaded files are stored as absolute URLs with whatever host was active at upload time
// (an old preview host or the prod domain); the object storage is shared, so rewriting the
// origin to this deployment's backend makes every `/api/files/...` URL load in any environment.
export function resolveMediaUrl(url) {
  if (!url) return url;
  const u = String(url).trim();
  if (!u) return u;
  const idx = u.indexOf("/api/files/");
  if (idx >= 0) return `${MEDIA_ORIGIN}${u.slice(idx)}`;
  if (u.startsWith("/api/") || u.startsWith("/uploads/")) return `${MEDIA_ORIGIN}${u}`;
  return u;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export async function api(path, opts = {}) {
  const { method = "GET", body, auth = true } = opts;
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  // ───────────────────────────────────────────────────────────────────────
  // ⚠️ CRITICAL — DO NOT REMOVE OR WEAKEN THIS TIMEOUT ⚠️
  // Every API request is aborted after 15s via AbortController. Without this,
  // a stalled backend leaves the app on an endless "Setting the table…" spinner
  // (a previously-shipped-then-regressed bug). On timeout we throw a tagged
  // error (err.timeout = true) so callers can show a clear message + Retry
  // instead of hanging. Keep the 15000ms limit and this behavior intact.
  // ───────────────────────────────────────────────────────────────────────
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s hard limit — must-not-remove
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      const err = new Error("The request timed out. Please check your connection and try again.");
      err.status = 0;
      err.timeout = true;
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  noteServerVersion(res.headers.get("X-App-Version"));
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    if (res.status === 401 && auth) {
      setToken(null);
      try { localStorage.removeItem("td_user"); } catch { /* ignore */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login?expired=1");
      }
    }
    const detail = data?.detail ?? data?.message ?? "Something went wrong";
    const msg = typeof detail === "string" ? detail : (detail?.message || JSON.stringify(detail));
    const err = new Error(msg);
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function uploadImage(file) {
  const t = getToken();
  const form = new FormData();
  form.append("file", file);
  // 60s hard limit so a stalled upload can't hang the UI forever (uploads are
  // heavier than JSON calls, so a longer limit than the 15s api() timeout).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);
  let res;
  try {
    res = await fetch(`${BASE}/upload?image_only=true`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: form,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      const err = new Error("The upload timed out. Please check your connection and try again.");
      err.timeout = true;
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) {
    let msg = "Upload failed. Please try again.";
    try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// Admin-only raw video upload for Member Spotlights (no image compression, larger files).
export async function uploadVideo(file) {
  const t = getToken();
  const form = new FormData();
  form.append("file", file);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 min for larger videos
  let res;
  try {
    res = await fetch(`${BASE}/admin/spotlights/upload-video`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: form,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      const err = new Error("The video upload timed out. Please check your connection and try again.");
      err.timeout = true;
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) {
    let msg = "Video upload failed";
    try { msg = (await res.json()).detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export { BASE };

export async function apiUpload(path, formData) {
  const t = getToken();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s hard limit — prevent stalled uploads hanging the UI
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: formData,
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") {
      const err = new Error("The upload timed out. Please check your connection and try again.");
      err.timeout = true;
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const detail = data?.detail ?? data?.message ?? "Upload failed";
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data;
}

export async function downloadFile(path, filename) {
  const t = getToken();
  const res = await fetch(`${BASE}${path}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- App version detection -------------------------------------------------
// We stamp every /api response with X-App-Version. The first version we observe
// becomes the "known" build for this session; if the server later reports a
// different token (admin published a new version), we flip updateAvailable and
// notify subscribers so the UI can prompt the user to log out and back in.
let _knownVersion = null;
let _updateAvailable = false;
const _versionListeners = new Set();

export function onVersionChange(cb) {
  _versionListeners.add(cb);
  return () => _versionListeners.delete(cb);
}

export function isUpdateAvailable() {
  return _updateAvailable;
}

export function noteServerVersion(v) {
  if (!v || _updateAvailable) return;
  if (_knownVersion === null) { _knownVersion = v; return; }
  if (v !== _knownVersion) {
    _updateAvailable = true;
    _versionListeners.forEach((cb) => { try { cb(true); } catch { /* noop */ } });
  }
}

export async function checkAppVersion() {
  try {
    const res = await fetch(`${BASE}/app/version`);
    const data = await res.json();
    noteServerVersion(data?.version);
  } catch { /* offline / transient, ignore */ }
}
